/**
 * React Query hooks for Runner enrollment operations.
 *
 * Runners are on-premises executors for MCP tools.
 * Uses the enrollment router at /mcp/agents.
 *
 * Sprint: 40, Task: S40-C01
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  RunnerListResponse,
  RunnerStatusResponse,
  EnrollmentInitiateRequest,
  EnrollmentInitiateResponse,
} from '../runner-types';

// =============================================================================
// Query Keys
// =============================================================================

export const runnerKeys = {
  all: ['runners'] as const,
  list: (tenantId: string) => [...runnerKeys.all, 'list', tenantId] as const,
  detail: (tenantId: string, runnerId: string) =>
    [...runnerKeys.all, 'detail', tenantId, runnerId] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * List all runners for the current tenant.
 */
export function useRunners(options?: {
  page?: number;
  pageSize?: number;
  status?: string;
  search?: string;
}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;

  return useQuery({
    queryKey: [...runnerKeys.list(tenantId!), { page, pageSize, status: options?.status, search: options?.search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (options?.status) params.set('status', options.status);
      if (options?.search) params.set('search', options.search);
      const { data } = await apiClient.get<{ agents: RunnerStatusResponse[]; total: number }>(
        `/api/v1/mcp/agents?${params.toString()}`,
      );
      return { runners: data.agents, total: data.total } as RunnerListResponse;
    },
    enabled: !!tenantId,
    refetchInterval: 15_000, // Poll every 15s for live status
  });
}

/**
 * Get a single runner by ID.
 */
export function useRunner(runnerId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: runnerKeys.detail(tenantId!, runnerId),
    queryFn: async () => {
      const { data } = await apiClient.get<RunnerStatusResponse>(`/api/v1/mcp/agents/${runnerId}`);
      return data;
    },
    enabled: !!tenantId && !!runnerId,
    refetchInterval: 10_000, // Poll every 10s for live status on detail page
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Initiate enrollment for a new runner.
 */
export function useEnrollRunner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (enrollData: EnrollmentInitiateRequest) => {
      const { data } = await apiClient.post<EnrollmentInitiateResponse>('/api/v1/mcp/agents/enroll', enrollData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: runnerKeys.all });
    },
  });
}

/**
 * Delete (revoke) a runner.
 */
export function useDeleteRunner() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (runnerId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      await apiClient.delete(`/api/v1/mcp/agents/${runnerId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: runnerKeys.all });
    },
  });
}

// =============================================================================
// Access Management Mutations
// =============================================================================

/**
 * Grant access to a runner (user or group).
 */
export function useGrantRunnerAccess() {
  return useMutation({
    mutationFn: async ({ runnerId, userId, groupId }: { runnerId: string; userId?: string; groupId?: string }) => {
      const { data } = await apiClient.post<{ message: string }>(`/api/v1/mcp/agents/${runnerId}/access`, {
        user_id: userId ?? null,
        group_id: groupId ?? null,
      });
      return data;
    },
  });
}

/**
 * Change runner execution mode (normal / validate_all).
 */
export function useChangeRunnerMode() {
  return useMutation({
    mutationFn: async ({ runnerId, mode }: { runnerId: string; mode: string }) => {
      const { data } = await apiClient.post<{ message: string }>(`/api/v1/mcp/agents/${runnerId}/mode`, { mode });
      return data;
    },
  });
}

/**
 * Revoke access from a runner (user or group).
 */
export function useRevokeRunnerAccess() {
  return useMutation({
    mutationFn: async ({ runnerId, userId, groupId }: { runnerId: string; userId?: string; groupId?: string }) => {
      return apiClient.delete(`/api/v1/mcp/agents/${runnerId}/access`, {
        data: {
          user_id: userId ?? null,
          group_id: groupId ?? null,
        },
      });
    },
  });
}

/**
 * Trigger certificate renewal for a runner.
 */
export function useRenewCertificate() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (runnerId: string) => {
      const { data } = await apiClient.post<{ agent_id: string; status: string }>(`/api/v1/mcp/agents/${runnerId}/renew`, {});
      return data;
    },
    onSuccess: (_, runnerId) => {
      queryClient.invalidateQueries({ queryKey: runnerKeys.detail(tenantId!, runnerId) });
    },
  });
}
